import { Check, ChevronDown, Usb } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { ConnectionIcon } from './ConnectionIcon';

const METHODS = [
  { id: 'retro-bridge', name: 'Retro-Bridge', label: 'Retro-Bridge', description: 'Wireless bridge' },
  { id: 'usb', name: 'USB', label: 'USB', description: 'Direct USB connection' },
  { id: 'hd15', name: 'HD-15', label: 'HD-15', description: 'HD-15 serial connection' },
] as const;

type Method = (typeof METHODS)[number];

function MethodIcon({ method, inverted = false }: { method: Method; inverted?: boolean }) {
  if (method.id === 'usb') return <Usb size={25} />;
  if (method.id === 'hd15') return <ConnectionIcon connection="RGBHV" />;
  return <>
    <img className="retro-bridge-mark light-mark" src={`/brand/retro-bridge-mark${inverted ? '-white' : ''}.svg`} alt="" width="24" height="15" />
    <img className="retro-bridge-mark dark-mark" src={`/brand/retro-bridge-mark${inverted ? '' : '-white'}.svg`} alt="" width="24" height="15" />
  </>;
}

// Presentation only: choosing a method never connects to or transfers to a device.
export function SendToRetroTinkButton() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Method>(METHODS[0]);
  const [opensAbove, setOpensAbove] = useState(false);
  const [offset, setOffset] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const initialFocus = useRef(0);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    optionRefs.current[initialFocus.current]?.focus();
    function closeOutside(event: PointerEvent): void {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  function showMenu(focusIndex = METHODS.indexOf(selected)): void {
    initialFocus.current = focusIndex;
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      const width = Math.min(300, window.innerWidth - 40);
      const spaceBelow = window.innerHeight - rect.bottom - 16;
      setOpensAbove(spaceBelow < 260 && rect.top - 84 > spaceBelow);
      setOffset(Math.max(20, Math.min(rect.right - width, window.innerWidth - width - 20)) - rect.left);
    }
    setOpen(true);
  }

  function closeMenu(): void {
    setOpen(false);
    triggerRef.current?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      return;
    }
    const index = optionRefs.current.indexOf(event.target as HTMLButtonElement);
    let next: number;
    if (event.key === 'ArrowDown') next = (index + 1) % METHODS.length;
    else if (event.key === 'ArrowUp') next = (index + METHODS.length - 1) % METHODS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = METHODS.length - 1;
    else return;
    event.preventDefault();
    optionRefs.current[next]?.focus();
  }

  return (
    <div className="send-to-device" ref={rootRef} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}>
      <button className="button send-to-device-button send-to-device-action" type="button" onClick={() => setOpen(false)}>
        <span className="send-to-device-icon" aria-hidden="true"><MethodIcon method={selected} inverted /></span>
        <span>Send to RetroTINK <span className="send-to-device-method">via {selected.name}</span></span>
      </button>
      <button
        className="button send-to-device-button send-to-device-trigger"
        type="button"
        ref={triggerRef}
        aria-label="Change connection method"
        title="Change connection method"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => open ? setOpen(false) : showMenu()}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            showMenu(event.key === 'ArrowUp' ? METHODS.length - 1 : 0);
          }
        }}
      ><ChevronDown size={15} aria-hidden="true" /></button>
      {open && (
        <div className={`send-to-device-panel${opensAbove ? ' opens-above' : ''}`} style={{ left: offset }}>
          <p>Choose connection</p>
          <div id={menuId} role="menu" aria-label="Send to RetroTINK" onKeyDown={handleKeyDown}>
            {METHODS.map((method, index) => (
              <button
                className="send-to-device-option"
                type="button"
                role="menuitemradio"
                tabIndex={-1}
                aria-checked={selected.id === method.id}
                aria-label={method.label}
                key={method.id}
                ref={(element) => { optionRefs.current[index] = element; }}
                onClick={() => { setSelected(method); closeMenu(); }}
              >
                <span className="send-to-device-icon" aria-hidden="true">
                  <MethodIcon method={method} />
                </span>
                <span className="send-to-device-copy"><strong>{method.label}</strong><small>{method.description}</small></span>
                {selected.id === method.id && <Check size={16} className="send-to-device-check" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
